import { sortBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  ModalAction,
  ModalBody,
  ModalFooter,
  ModalFooterActions,
  ModalHeader,
  ModalV2,
  Text,
} from '@electricjs/arc';

import { Employee } from '@/types/employees';

import EmployeeCheckboxList from './EmployeeCheckboxList';
import { CopyButton } from '@common';
import { getDisplayFullName } from 'common/utils/getFullName';

type AppInviteModalProps<GenericEmployee extends Employee> = {
  visible: boolean;
  hide: () => void;
  employees: GenericEmployee[];
  showCheckList: boolean;
  selectedEmployees: GenericEmployee[];
  setSelectedEmployees: React.Dispatch<React.SetStateAction<GenericEmployee[]>>;
  isLoading: boolean;
  handleCancel: () => void;
  handleSubmit: () => void;
  instructionsToCopy: string;
  hasMdmEntitlement?: boolean;
  includeEmail?: boolean;
  includeDevice?: boolean;
  title?: string;
  description?: string;
};

const AppInviteModal = <GenericEmployee extends Employee>({
  visible,
  hide,
  employees,
  showCheckList,
  selectedEmployees,
  setSelectedEmployees,
  isLoading,
  handleCancel,
  handleSubmit,
  hasMdmEntitlement,
  instructionsToCopy,
  includeDevice = true,
  includeEmail = false,
  title,
  description,
}: AppInviteModalProps<GenericEmployee>) => {
  const { t } = useTranslation('assets');

  const descriptionIfQS = t('appInviteModal.descriptionQS');
  const descriptionIfMdm = t('appInviteModal.descriptionMDM');

  return (
    <ModalV2
      visible={visible}
      hide={hide}
      maxWidth={includeDevice && includeEmail ? '55%' : '59rem'}
      maxHeight="75rem"
      ariaLabelledby="app-invite">
      <ModalHeader>
        <Text variant="heading-2">{title || t('appInviteModal.title')}</Text>
      </ModalHeader>
      <ModalBody overflowY={showCheckList ? 'scroll' : undefined}>
        <Text maxWidth="49rem">
          {description ||
            (hasMdmEntitlement ? descriptionIfMdm : descriptionIfQS)}
        </Text>
        {showCheckList && (
          <EmployeeCheckboxList
            includeDevice={includeDevice}
            includeEmail={includeEmail}
            employees={sortBy(employees, e =>
              getDisplayFullName(e).toLowerCase()
            )}
            selectedEmployees={selectedEmployees}
            setSelectedEmployees={setSelectedEmployees}
          />
        )}
      </ModalBody>
      <ModalFooter>
        <ModalFooterActions>
          <ModalAction
            id="employee-profile-invite-modal-cancel"
            variant="text"
            disabled={isLoading}
            onClick={handleCancel}>
            {t('appInviteModal.cancel')}
          </ModalAction>
          <CopyButton
            id="app-enrollment-instructions-button"
            textToCopy={instructionsToCopy}
            disabled={selectedEmployees.length === 0 || isLoading}>
            {t('appInviteModal.copyInstructions')}
          </CopyButton>
          <ModalAction
            id="employee-profile-invite-modal-submit"
            loading={isLoading}
            disabled={selectedEmployees.length === 0}
            onClick={handleSubmit}>
            {t('appInviteModal.sendEmail')}
          </ModalAction>
        </ModalFooterActions>
      </ModalFooter>
    </ModalV2>
  );
};

export default AppInviteModal;
